// components/UndoToast.tsx
import React, { useEffect, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useUndoToast } from '../hooks/useUndoToast';
import { IconButton, focusRing, cx } from './ui';

const toastClass =
  // On a phone it sits above the tab bar and the floating Add chore button.
  'fixed bottom-[calc(9.5rem+env(safe-area-inset-bottom))] sm:bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-xl bg-gray-900 pl-4 text-sm text-white shadow-xl dark:bg-gray-800 dark:shadow-none dark:ring-1 dark:ring-gray-700 max-w-[calc(100vw-2rem)]';

const AUTO_DISMISS_MS = 6000;
const TOO_LATE_DISMISS_MS = 3000;

export const UndoToast: React.FC = () => {
  const { undoableCompletion, undoMarkChoreDone, dismissUndoableCompletion } = useUndoToast();
  // Set locally (rather than via chores.error) when undo loses the race with a sync — that's an
  // expected, low-stakes outcome for this one toast, not something the rest of the UI should
  // react to (chores.error blanks the whole chore list, which would be a jarring overreaction here).
  const [tooLate, setTooLate] = useState(false);

  useEffect(() => {
    if (!undoableCompletion) return;
    const timer = setTimeout(dismissUndoableCompletion, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [undoableCompletion, dismissUndoableCompletion]);

  useEffect(() => {
    if (!tooLate) return;
    const timer = setTimeout(() => setTooLate(false), TOO_LATE_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [tooLate]);

  const handleUndo = async () => {
    const undone = await undoMarkChoreDone();
    if (!undone) setTooLate(true);
  };

  if (tooLate) {
    return (
      <div className={cx(toastClass, 'pr-4 py-3')} role="status">
        <span>Already synced to the cloud — can&apos;t be undone.</span>
      </div>
    );
  }

  if (!undoableCompletion) return null;

  return (
    <div className={cx(toastClass, 'pr-2 py-2')} role="status">
      <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500">
        <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      <span className="truncate">
        {undoableCompletion.choreName ? `“${undoableCompletion.choreName}” marked done` : 'Chore marked done'}
      </span>
      <button
        type="button"
        onClick={handleUndo}
        className={cx('rounded-lg px-2 py-1 font-semibold text-indigo-300 hover:bg-white/10 hover:text-indigo-200', focusRing)}
      >
        Undo
      </button>
      <IconButton label="Dismiss" icon={XMarkIcon} tone="inverse" size="sm" onClick={dismissUndoableCompletion} />
    </div>
  );
};

export default UndoToast;
